/* eslint-disable no-plusplus */
import { useQuery, useMutation } from "@tanstack/react-query";
import QueryGlobalInterface from "interfaces/queryInterface";
import { toast } from "react-toastify";
import instance from "services/axiosServices";
import leadsWebEnviroment from "utils/leadsWebEnviroment";

export interface ControleVisitaItem {
  id: string;
  empreendimento: string;
  empresaVendas: string;
  localVenda: string;
  email: string;
  telefone: string;
  cep?: string | null;
  bairroComercial: string | null;
  bairro: string | null;
  idLeadCorretor365: string | null;
  nomeLead: string;
  corretor: string;
  corretorOrigem: string | null;
  tipoAtendimento: string;
  dataHoraVisita: string;
  registradoPor: string;
  origem: string | null;
  canalOrigem: string;
  podeVisualizar: boolean;
}

export interface FiltrosControleVisitas {
  palavraChave?: string;
  dataInicioVisita?: Date;
  dataFimVisita?: Date;
  idsOrigem?: string[];
  idsCanal?: string[];
  idsTipoAtendimento?: string[];
  idsEmpreendimento?: string[];
  idsEquipe?: string[];
  idsCorretorOrigem?: string[];
}

export interface ListarControleVisitasParams {
  paginaAtual: number;
  totalItensRetorno: number;
  enabled?: boolean;
  filtros?: FiltrosControleVisitas;
}

export interface ListarControleVisitasResponse {
  success: boolean;
  data: ControleVisitaItem[];
  pagination?: {
    currentPage: number;
    quantityPages: number;
    totalItems: number;
    totalItemsPage: number;
  };
}

export function useListarControleVisitas(
  params: ListarControleVisitasParams & QueryGlobalInterface
) {
  const fetchData = async () => {
    const LISTAR_CONTROLE_VISITAS = `${leadsWebEnviroment}/commom/leads/controle-visitas/v1`;

    const body = {
      paginaAtual: params.paginaAtual,
      totalItensRetorno: params.totalItensRetorno,
      ...(params.filtros?.palavraChave && {
        palavraChave: params.filtros.palavraChave,
      }),
      ...(params.filtros?.dataInicioVisita && {
        dataInicioVisita: params.filtros.dataInicioVisita,
      }),
      ...(params.filtros?.dataFimVisita && {
        dataFimVisita: params.filtros.dataFimVisita,
      }),
      ...(params.filtros?.idsOrigem &&
        params.filtros.idsOrigem.length > 0 && {
          idsOrigem: params.filtros.idsOrigem,
        }),
      ...(params.filtros?.idsCanal &&
        params.filtros.idsCanal.length > 0 && {
          idsCanal: params.filtros.idsCanal,
        }),
      ...(params.filtros?.idsTipoAtendimento &&
        params.filtros.idsTipoAtendimento.length > 0 && {
          idsTipoAtendimento: params.filtros.idsTipoAtendimento,
        }),
      ...(params.filtros?.idsEmpreendimento &&
        params.filtros.idsEmpreendimento.length > 0 && {
          idsEmpreendimento: params.filtros.idsEmpreendimento,
        }),
      ...(params.filtros?.idsEquipe &&
        params.filtros.idsEquipe.length > 0 && {
          idsEquipe: params.filtros.idsEquipe,
        }),
      ...(params.filtros?.idsCorretorOrigem &&
        params.filtros.idsCorretorOrigem.length > 0 && {
          idsCorretorOrigem: params.filtros.idsCorretorOrigem,
        }),
    };

    const response = await instance.post<ListarControleVisitasResponse>(
      LISTAR_CONTROLE_VISITAS,
      body
    );

    return {
      data: response?.data?.data || [],
      pagination: response?.data?.pagination,
    };
  };

  return useQuery({
    queryFn: () => fetchData(),
    queryKey: [
      params.queryKey,
      params.paginaAtual,
      params.totalItensRetorno,
      params.filtros,
    ],
    refetchOnWindowFocus: false,
    enabled: params.enabled ?? false,
    retry: false,
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => `${item?.message}`).join("\n")
        : "Erro desconhecido ao listar controle de visitas";

      toast.error(
        `Erro ao listar controle de visitas. Motivos:\n${errorMessages}`,
        { autoClose: 10000 }
      );
    },
  });
}

/* ===========================
   EXPORTAR CONTROLE VISITAS
   =========================== */

export interface ExportarControleVisitasBody {
  palavraChave?: string;
  dataInicioVisita?: Date;
  dataFimVisita?: Date;
  idsOrigem?: string[];
  idsCanal?: string[];
  idsTipoAtendimento?: string[];
  idsEmpreendimento?: string[];
  idsEquipe?: string[];
  idsCorretorOrigem?: string[];
}

export interface ExportarControleVisitasParams {
  /** filtros iguais aos do listar */
  filtros?: FiltrosControleVisitas;
}

function extractFilenameFromDisposition(disposition?: string): string | null {
  if (!disposition) return null;

  // tenta filename*=UTF-8''...
  const matchUtf8 = disposition.match(/filename\*\\=UTF-8''([^;]+)/i);
  if (matchUtf8?.[1]) return decodeURIComponent(matchUtf8[1].replace(/"/g, ""));

  // fallback filename="..."
  const match = disposition.match(/filename\\=\\"?([^\\";]+)\\"?/i);
  if (match?.[1]) return match[1];

  return null;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function useExportarControleVisitas(
  params: ExportarControleVisitasParams
) {
  const EXPORTAR_CONTROLE_VISITAS = `${leadsWebEnviroment}/commom/leads/exportar-controle-visitas/v1`;

  const buildBody = (): ExportarControleVisitasBody => {
    const f = params.filtros;
    const body: ExportarControleVisitasBody = {};

    if (f?.palavraChave) body.palavraChave = f.palavraChave;
    if (f?.dataInicioVisita) body.dataInicioVisita = f.dataInicioVisita;
    if (f?.dataFimVisita) body.dataFimVisita = f.dataFimVisita;
    if (f?.idsOrigem?.length) body.idsOrigem = f.idsOrigem;
    if (f?.idsCanal?.length) body.idsCanal = f.idsCanal;
    if (f?.idsTipoAtendimento?.length)
      body.idsTipoAtendimento = f.idsTipoAtendimento;
    if (f?.idsEmpreendimento?.length)
      body.idsEmpreendimento = f.idsEmpreendimento;
    if (f?.idsEquipe?.length) body.idsEquipe = f.idsEquipe;
    if (f?.idsCorretorOrigem?.length)
      body.idsCorretorOrigem = f.idsCorretorOrigem;

    return body;
  };

  return useMutation({
    mutationKey: ["exportar-controle-visitas", params.filtros],
    mutationFn: async () => {
      const body = buildBody();

      const response = await instance.post(EXPORTAR_CONTROLE_VISITAS, body);

      const resData = response?.data;

      if (!resData?.success || !resData?.data?.base64Planilha) {
        const msgs = Array.isArray(resData?.notifications)
          ? resData.notifications.map((n: any) => n?.message).join("\n")
          : "Erro ao exportar controle de visitas";
        throw new Error(msgs);
      }

      const base64 = resData.data.base64Planilha;
      const byteCharacters = atob(base64);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const filename = `controle-visitas-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      const blob = new Blob([byteNumbers], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadBlob(blob, filename);

      return { filename };
    },
    retry: false,
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => `${item?.message}`).join("\n")
        : "Erro desconhecido ao exportar controle de visitas";

      toast.error(
        `Erro ao exportar controle de visitas. Motivos:\n${errorMessages}`,
        { autoClose: 10000 }
      );
    },
  });
}
